import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { MessageCircle, X, RefreshCw } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../services/api';
import { useUser } from '../context/UserContext';

const ChatNotification = () => {
  const { user } = useUser();
  const location = useLocation();
  const [unreadCount, setUnreadCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [firstUnreadChatId, setFirstUnreadChatId] = useState(null);
  const [lastCheckTime, setLastCheckTime] = useState(0);
  const [justLeftChat, setJustLeftChat] = useState(false);

  useEffect(() => {
    // Don't check for unread messages if user is not authenticated
    if (!user) {
      return;
    }

    // Always hide notification when in chat routes
    if (location.pathname.startsWith('/chat')) {
      setIsVisible(false);
      setJustLeftChat(false);
      return;
    }

    // If we just left a chat, wait a bit before checking again
    if (justLeftChat) {
      const timer = setTimeout(() => {
        setJustLeftChat(false);
      }, 1000); // Increased delay to 1 second
      return () => clearTimeout(timer);
    }

    // Check for unread messages
    const checkUnreadMessages = async () => {
      // Debounce: don't check more than once every 3 seconds
      const now = Date.now();
      if (now - lastCheckTime < 3000) {
        return;
      }
      setLastCheckTime(now);
      
      try {
        setLoading(true);
        
        // Get both unread count and conversations to find first unread chat
        const [unreadResponse, conversationsResponse] = await Promise.all([
          api.getUnreadCount(),
          api.getConversations()
        ]);
        
        const count = unreadResponse.data?.unreadCount || 0;
        setUnreadCount(count);
        
        // Find the first chat with unread messages
        const conversations = conversationsResponse.data || [];
        const firstUnreadChat = conversations.find(conv => conv.unreadCount > 0);
        setFirstUnreadChatId(firstUnreadChat?._id || null);
        
        console.log('ChatNotification: Periodic check', {
          count,
          isVisible,
          justLeftChat,
          pathname: location.pathname,
          unreadResponse: unreadResponse.data,
          conversations: conversations.map(c => ({ id: c._id, unreadCount: c.unreadCount }))
        });
        
        // Show notification if there are unread messages
        if (count > 0 && !justLeftChat && !location.pathname.startsWith('/chat')) {
          setIsVisible(true);
        } else {
          setIsVisible(false);
        }
      } catch (error) {
        // Only log the error, don't show notification for connection issues
        if (error.message !== 'Failed to fetch') {
          console.error('Error checking unread messages:', error);
        }
      } finally {
        setLoading(false);
      }
    };

    // Initial check
    checkUnreadMessages();

    // Set up interval for periodic checks (every 30 seconds)
    const interval = setInterval(checkUnreadMessages, 30000);

    return () => clearInterval(interval);
  }, [user, location.pathname, justLeftChat, lastCheckTime]);

  // Immediate check when user changes
  useEffect(() => {
    if (!user) return;
    
    const checkUnreadMessages = async () => {
      try {
        const [unreadResponse, conversationsResponse] = await Promise.all([
          api.getUnreadCount(),
          api.getConversations()
        ]);
        
        const count = unreadResponse.data?.unreadCount || 0;
        setUnreadCount(count);
        
        const conversations = conversationsResponse.data || [];
        const firstUnreadChat = conversations.find(conv => conv.unreadCount > 0);
        setFirstUnreadChatId(firstUnreadChat?._id || null);
        
        if (count > 0 && !justLeftChat && !location.pathname.startsWith('/chat')) {
          setIsVisible(true);
        } else {
          setIsVisible(false);
        }
      } catch (error) {
        if (error.message !== 'Failed to fetch') {
          console.error('Error checking unread messages on user change:', error);
        }
      }
    };
    
    checkUnreadMessages();
  }, [user]);

  // Track when we enter/leave chat routes - FIXED: removed justLeftChat from dependencies
  useEffect(() => {
    if (location.pathname.startsWith('/chat')) {
      setJustLeftChat(false);
      setIsVisible(false); // Ensure notification is hidden in chat routes
    } else {
      // We left a chat route, set the flag to prevent immediate re-showing
      setJustLeftChat(true);
      // Clear the flag after a delay
      const timer = setTimeout(() => {
        setJustLeftChat(false);
      }, 1000); // Increased delay to 1 second
      return () => clearTimeout(timer);
    }
  }, [location.pathname]); // Removed justLeftChat from dependencies

  // Listen for messages being read
  useEffect(() => {
    const handleMessagesRead = async (event) => {
      if (!user) return;
      
      console.log('ChatNotification: messagesRead event received', event.detail);
      
      // Don't check if we're in a chat route
      if (location.pathname.startsWith('/chat')) {
        setIsVisible(false);
        return;
      }
      
      // Debounce: don't check more than once every 2 seconds
      const now = Date.now();
      if (now - lastCheckTime < 2000) {
        return;
      }
      setLastCheckTime(now);
      
      try {
        // Check unread count immediately when messages are read
        const [unreadResponse, conversationsResponse] = await Promise.all([
          api.getUnreadCount(),
          api.getConversations()
        ]);
        
        const count = unreadResponse.data?.unreadCount || 0;
        setUnreadCount(count);
        
        const conversations = conversationsResponse.data || [];
        const firstUnreadChat = conversations.find(conv => conv.unreadCount > 0);
        setFirstUnreadChatId(firstUnreadChat?._id || null);
        
        console.log('ChatNotification: After messagesRead check', {
          count,
          isVisible,
          justLeftChat,
          pathname: location.pathname,
          unreadResponse: unreadResponse.data,
          conversations: conversations
        });
        
        // Show/hide notification based on unread count
        if (count === 0) {
          setIsVisible(false);
        } else if (!justLeftChat) {
          setIsVisible(true);
        }
      } catch (error) {
        if (error.message !== 'Failed to fetch') {
          console.error('Error checking unread messages after read:', error);
        }
      }
    };

    window.addEventListener('messagesRead', handleMessagesRead);
    
    return () => {
      window.removeEventListener('messagesRead', handleMessagesRead);
    };
  }, [user, location.pathname, lastCheckTime, justLeftChat]);

  // Don't show notifications for unauthenticated users
  if (!user) {
    return null;
  }

  // Don't show notification if we're already in a chat route
  if (location.pathname.startsWith('/chat')) {
    return null;
  }

  // Don't show if we just left a chat
  if (justLeftChat) {
    return null;
  }

  const handleDismiss = () => {
    setIsVisible(false);
  };

  const handleClick = () => {
    setIsVisible(false);
    setJustLeftChat(true);
    // The Link will handle navigation to the specific chat
    // The notification will be hidden when entering the chat route
  };

  const handleRefresh = async () => {
    console.log('ChatNotification: Manual refresh requested');
    try {
      const [unreadResponse, conversationsResponse] = await Promise.all([
        api.getUnreadCount(),
        api.getConversations()
      ]);
      
      const count = unreadResponse.data?.unreadCount || 0;
      setUnreadCount(count);
      
      const conversations = conversationsResponse.data || [];
      const firstUnreadChat = conversations.find(conv => conv.unreadCount > 0);
      setFirstUnreadChatId(firstUnreadChat?._id || null);
      
      console.log('ChatNotification: Manual refresh result', {
        count,
        unreadResponse: unreadResponse.data,
        conversations: conversations.map(c => ({ id: c._id, unreadCount: c.unreadCount }))
      });
      
      if (count > 0 && !justLeftChat && !location.pathname.startsWith('/chat')) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    } catch (error) {
      console.error('Error in manual refresh:', error);
    }
  };
  
  if (!isVisible || unreadCount === 0) {
    return null;
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 50, scale: 0.8 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 50, scale: 0.8 }}
        className="fixed bottom-6 right-6 z-50"
      >
        <Link
          to={firstUnreadChatId ? `/chat/${firstUnreadChatId}` : "/chat"}
          onClick={handleClick}
          className="block"
        >
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-4 min-w-[280px]">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="relative">
                  <MessageCircle className="w-6 h-6 text-primary-500" />
                  {unreadCount > 0 && (
                    <div className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-medium">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </div>
                  )}
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white">
                    New Messages
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    You have {unreadCount} unread message{unreadCount > 1 ? 's' : ''}
                  </p>
                </div>
              </div>
              
              <button
                onClick={(e) => {
                  e.preventDefault();
                  handleDismiss();
                }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
              
              <button
                onClick={(e) => {
                  e.preventDefault();
                  handleRefresh();
                }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors ml-2"
                title="Refresh unread count"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>
            
            <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Click to view your conversations
              </p>
            </div>
          </div>
        </Link>
      </motion.div>
    </AnimatePresence>
  );
};

export default ChatNotification; 